// StructureFilter — the toolbar Filter button + anchored popover with two
// multi-select sections: NODE (unit checkboxes, with a nested name search) and
// TAGS (tag checkboxes). Selection state is owned by the page; this component is
// presentational. Rows are hand-rolled labels reusing the checkbox visual
// classes so each can carry custom content (name + level, or tag icon + title).

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, Input } from '@/components/common';
import { useLevelConfig } from '@/api/endpoints/levelsApi';
import type { OrgUnit } from '@/mocks/structure';
import type { Tag } from '@/api/endpoints/tagsApi';

interface StructureFilterProps {
  units: OrgUnit[];
  tags: Tag[];
  nodeIds: Set<string>;
  tagIds: Set<string>;
  onToggleNode: (id: string) => void;
  onToggleTag: (id: string) => void;
}

const StructureFilter = ({
  units,
  tags,
  nodeIds,
  tagIds,
  onToggleNode,
  onToggleTag,
}: StructureFilterProps) => {
  const { t } = useTranslation();
  const levelConfig = useLevelConfig();
  const [open, setOpen] = useState(false);
  const [nodeSearch, setNodeSearch] = useState('');

  const shownUnits = useMemo(() => {
    const q = nodeSearch.trim().toLowerCase();
    return q ? units.filter((u) => u.name.toLowerCase().includes(q)) : units;
  }, [units, nodeSearch]);

  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align='end'
      width={300}
      className='structure-filter'
      trigger={
        <button
          type='button'
          className={`structure-filter__trigger${open ? ' is-open' : ''}`}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name='list-filter' size={16} />
          {t('structure_filter')}
          <Icon
            name={open ? 'chevron-up' : 'chevron-down'}
            size={16}
            className='structure-filter__caret'
          />
        </button>
      }
    >
      <div className='structure-filter__section'>
        <span className='structure-filter__label'>{t('structure_filter_node')}</span>
        <Input
          size='sm'
          iconStart='search'
          placeholder={t('structure_filter_find')}
          value={nodeSearch}
          onChange={(e) => setNodeSearch(e.target.value)}
        />
        <div className='structure-filter__list'>
          {shownUnits.map((u) => {
            const level = levelConfig(u.level);
            return (
              <label key={u.id} className='structure-filter__opt'>
                <input
                  type='checkbox'
                  className='app-checkbox__input'
                  checked={nodeIds.has(u.id)}
                  onChange={() => onToggleNode(u.id)}
                />
                <span className='app-checkbox__box' aria-hidden='true'>
                  <Icon name='check' size={14} className='app-checkbox__check' />
                </span>
                <span className='structure-filter__opt-body'>
                  <span className='structure-filter__opt-name'>{u.name}</span>
                  <span className='structure-filter__opt-sub'>{level.name}</span>
                </span>
              </label>
            );
          })}
          {shownUnits.length === 0 && (
            <span className='structure-filter__empty'>{t('structure_no_results')}</span>
          )}
        </div>
      </div>

      <div className='structure-filter__section'>
        <span className='structure-filter__label'>{t('structure_filter_tags')}</span>
        {tags.length === 0 ? (
          <span className='structure-filter__empty'>{t('structure_filter_tags_empty')}</span>
        ) : (
          tags.map((tag) => (
            <label key={tag.id} className='structure-filter__opt'>
              <input
                type='checkbox'
                className='app-checkbox__input'
                checked={tagIds.has(tag.id)}
                onChange={() => onToggleTag(tag.id)}
              />
              <span className='app-checkbox__box' aria-hidden='true'>
                <Icon name='check' size={14} className='app-checkbox__check' />
              </span>
              <Icon name='tag' size={16} className='structure-filter__opt-icon' />
              <span className='structure-filter__opt-name'>{tag.title}</span>
            </label>
          ))
        )}
      </div>
    </Dropdown>
  );
};

export default StructureFilter;
